import { useState, useRef, useEffect } from "react";
import "./MultiSelectDropdown.css";

/**
 * MultiSelectDropdown
 *
 * React port of the Angular shared component
 * src/app/shared/multiselect-dropdown/multiselect-dropdown.component.ts.
 * Logic ported verbatim:
 *   - maxSelections (0 = no limit): once the cap is exceeded, only the
 *     FIRST maxSelections ids are kept (slice-to-cap), matching
 *     onModelChange()'s `this.model = this.model.slice(0, this.maxSelections)`.
 *   - getPlaceholderText(): "<title>Select options (max N)" when capped,
 *     "<title>Select options" otherwise - same string shape as the source's
 *     `title + 'Select options' [+ ' (max N)']`.
 *   - getSelectedText(): empty when nothing selected, the single option's
 *     name when exactly one is selected, "<n> selected" otherwise.
 *
 * Used by SkillsMatrixComparison.jsx for Vertical Stack (max 3), Sub Stack
 * (no cap) and Job Title (max 5) - the three multiselects confirmed in
 * skillset-matrix.component.html. NOT used by JobSpecComparison.jsx, which
 * is single-select in the source (see JobSpecComparison.jsx header note).
 */
export default function MultiSelectDropdown({
  options = [],
  model = [],
  title = "",
  maxSelections = 0,
  disabled = false,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selectedIds = model || [];

  function toggle(id) {
    let next;
    if (selectedIds.includes(id)) {
      next = selectedIds.filter((x) => x !== id);
    } else {
      next = [...selectedIds, id];
      // Slice-to-cap, verbatim port of onModelChange()'s max-selections rule.
      if (maxSelections > 0 && next.length > maxSelections) {
        next = next.slice(0, maxSelections);
      }
    }
    onChange?.(next);
  }

  function getSelectedText() {
    if (!selectedIds.length) return "";
    if (selectedIds.length === 1) {
      const opt = options.find((o) => o.id === selectedIds[0]);
      return opt?.name || "";
    }
    return `${selectedIds.length} selected`;
  }

  function getPlaceholderText() {
    // FIXED: no `${title}` prefix here - the trigger already renders the
    // title in its own .msd-label span, so including it in the placeholder
    // doubled it ("VERTICAL STACK Vertical Stack Select options...").
    let placeholder = "Select options";
    if (maxSelections > 0) placeholder += ` (max ${maxSelections})`;
    return placeholder;
  }

  const atCap = maxSelections > 0 && selectedIds.length >= maxSelections;
  const selectedText = getSelectedText();

  return (
    <div className={`msd-field${disabled ? " msd-field-disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className="msd-btn"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className="msd-label">{title}</span>
        <span className="msd-value">{selectedText || getPlaceholderText()}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="msd-chevron">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && !disabled && (
        <div className="msd-menu">
          {options.length === 0 && <div className="msd-menu-empty">No options</div>}
          {options.map((opt) => {
            const checked = selectedIds.includes(opt.id);
            const blocked = !checked && atCap;
            return (
              <button
                type="button"
                key={opt.id}
                className={`msd-menu-item${blocked ? " msd-menu-item-blocked" : ""}`}
                onClick={() => !blocked && toggle(opt.id)}
                disabled={blocked}
              >
                <span className={`msd-check${checked ? " msd-check-on" : ""}`}>
                  {checked && (
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                {opt.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
